import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
  listAdminMagazine,
  upsertMagazine,
  deleteMagazine,
  type MagazineArticleRow,
} from "@/lib/magazine.functions";

export const Route = createFileRoute("/admin/magazine")({
  component: AdminMagazinePage,
});

function blocksToRaw(blocks: MagazineArticleRow["body"]): string {
  return blocks
    .map((b) => {
      if (b.type === "hr") return "---";
      if (b.type === "h2") return `## ${b.text}`;
      if (b.type === "h3") return `### ${b.text}`;
      if (b.type === "blockquote") return `> ${b.text}`;
      return b.text;
    })
    .join("\n\n");
}

const EMPTY = {
  id: "" as string,
  slug: "",
  magazine: "",
  title: "",
  dek: "",
  excerpt: "",
  body_raw: "",
  display_date: "",
  iso_date: new Date().toISOString().slice(0, 10),
  published: true,
};

function AdminMagazinePage() {
  const list = useServerFn(listAdminMagazine);
  const upsert = useServerFn(upsertMagazine);
  const del = useServerFn(deleteMagazine);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-magazine"],
    queryFn: () => list(),
  });

  const [form, setForm] = useState({ ...EMPTY });
  const [msg, setMsg] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form };
      const id = payload.id || undefined;
      const { id: _omit, ...rest } = payload;
      return upsert({ data: { ...rest, id } });
    },
    onSuccess: () => {
      setMsg("Kaydedildi ✓");
      setForm({ ...EMPTY });
      refetch();
      setTimeout(() => setMsg(null), 2000);
    },
    onError: (e: Error) => setMsg("Hata: " + e.message),
  });

  const onEdit = (a: MagazineArticleRow) => {
    setForm({
      id: a.id,
      slug: a.slug,
      magazine: a.magazine,
      title: a.title,
      dek: a.dek,
      excerpt: a.excerpt,
      body_raw: blocksToRaw(a.body),
      display_date: a.display_date,
      iso_date: a.iso_date,
      published: a.published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (a: MagazineArticleRow) => {
    if (!confirm(`"${a.title}" silinsin mi?`)) return;
    await del({ data: { id: a.id } });
    refetch();
  };

  const articles = data ?? [];
  const isEditing = !!form.id;

  return (
    <div className="space-y-10">
      <section>
        <h1 className="font-serif text-3xl mb-2">Dergi Yazıları</h1>
        <p className="text-sm text-ink/85 mb-6">
          Ana sayfadaki "Latest Magazine Articles" alanı en son 3 yayında olan yazıyı gösterir.
          Tüm liste{" "}
          <Link to="/dergi" className="underline">
            /dergi
          </Link>{" "}
          sayfasında listelenir.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-4 border border-border rounded-md p-5 bg-paper"
        >
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif text-xl">
              {isEditing ? "Yazıyı düzenle" : "Yeni yazı"}
            </h2>
            {isEditing && (
              <button
                type="button"
                onClick={() => setForm({ ...EMPTY })}
                className="text-xs underline text-ink/60"
              >
                Yeni yazıya geç
              </button>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">
                Başlık
              </span>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">
                Slug (URL)
              </span>
              <input
                required
                value={form.slug}
                onChange={(e) =>
                  setForm({ ...form, slug: e.target.value.toLowerCase() })
                }
                placeholder="yeni-yazi-baslik"
                className="w-full border border-border rounded px-3 py-2 font-mono text-xs"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">
                Dergi
              </span>
              <input
                required
                value={form.magazine}
                onChange={(e) => setForm({ ...form, magazine: e.target.value })}
                placeholder="Centre / The Brand Map / The Newsight"
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">
                Görünen tarih
              </span>
              <input
                value={form.display_date}
                onChange={(e) =>
                  setForm({ ...form, display_date: e.target.value })
                }
                placeholder="Ocak 2026"
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">
                Tarih (sıralama)
              </span>
              <input
                required
                type="date"
                value={form.iso_date}
                onChange={(e) => setForm({ ...form, iso_date: e.target.value })}
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
            <label className="flex items-center gap-2 text-sm mt-6">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) =>
                  setForm({ ...form, published: e.target.checked })
                }
              />
              Yayında
            </label>
          </div>

          <label className="block text-sm">
            <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">
              Özet (kart için kısa açıklama)
            </span>
            <textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
              className="w-full border border-border rounded px-3 py-2"
            />
          </label>

          <label className="block text-sm">
            <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">
              Dek / Kicker (yazı sayfasında italik standfirst)
            </span>
            <textarea
              value={form.dek}
              onChange={(e) => setForm({ ...form, dek: e.target.value })}
              rows={2}
              className="w-full border border-border rounded px-3 py-2"
            />
          </label>

          <label className="block text-sm">
            <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">
              Yazı içeriği
            </span>
            <textarea
              required
              value={form.body_raw}
              onChange={(e) => setForm({ ...form, body_raw: e.target.value })}
              rows={18}
              placeholder={
                "Her boş satır yeni bir paragraf başlatır.\n\n## Alt başlık (H2)\n\n### Daha küçük başlık (H3)\n\n> Bu bir alıntıdır\n\n---\n\nBölme çizgisi için üç tire."
              }
              className="w-full border border-border rounded px-3 py-2 font-mono text-xs leading-relaxed"
            />
            <span className="text-[11px] text-ink/85 mt-1 block">
              Boş satır = paragraf · <code>## </code> başlık · <code>### </code>
              alt başlık · <code>&gt; </code> alıntı · <code>---</code> ayırıcı
            </span>
          </label>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={save.isPending}
              className="px-5 py-2.5 bg-ink text-paper rounded-full text-xs uppercase tracking-[0.2em] disabled:opacity-50"
            >
              {save.isPending ? "Kaydediliyor..." : isEditing ? "Güncelle" : "Yayınla"}
            </button>
            {msg && <span className="text-sm text-ink/70">{msg}</span>}
          </div>
        </form>
      </section>

      <section>
        <h2 className="font-serif text-2xl mb-4">Mevcut yazılar</h2>
        {isLoading ? (
          <p className="text-sm text-ink/60">Yükleniyor...</p>
        ) : articles.length === 0 ? (
          <p className="text-sm text-ink/60">Henüz yazı eklenmedi.</p>
        ) : (
          <ul className="divide-y divide-border">
            {articles.map((a) => (
              <li key={a.id} className="py-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded ${
                        a.published ? "bg-moss/10 text-moss" : "bg-ink/10 text-ink/60"
                      }`}
                    >
                      {a.published ? "Yayında" : "Taslak"}
                    </span>
                    <span className="text-xs text-ink/85">{a.magazine}</span>
                    <span className="text-xs text-ink/85">· {a.iso_date}</span>
                  </div>
                  <div className="font-serif text-lg mt-1 truncate">{a.title}</div>
                </div>
                <button
                  onClick={() => onEdit(a)}
                  className="text-xs underline text-ink/70 hover:text-ink"
                >
                  Düzenle
                </button>
                <button
                  onClick={() => onDelete(a)}
                  className="text-xs text-ink/85 hover:text-clay"
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
